import isNumber from 'lodash/isNumber';
import React, { useEffect, useState } from 'react';

import { SizeType } from 'craft/components/Size';
import { CSSValue } from 'craft/components/Spacing/Spacing';
import { SizeStepper } from 'craft/containers/Toolbox/sections/Size/SizeStepper';
import { useResponsiveCss } from 'craft/hooks';
import { useViewportBreakpoint } from 'craft/state';
import { CommonNodeProperties, CssSelectorId } from 'craft/types';

interface Props {
  nodeIds: string[];
  property: string;
  label: string;
  isInGroup: any;
  cssSelectorId?: CssSelectorId;
}

const parseValue = (value?: CSSValue) => {
  if (value === 'auto') {
    return { type: SizeType.Auto, value: undefined };
  }
  // default to PX if value is numeric
  if (isNumber(value)) {
    return { type: SizeType.Pixel, value };
  }
  // since we know it's not a number, we're not getting a false positive from a 0
  if (!value) {
    return { type: undefined, value: undefined };
  }

  // handle string values
  const parsed = parseInt(value.replace(/px|%/, ''), 10);

  // fallback to unset if something goes wrong
  if (Number.isNaN(parsed)) {
    return { type: undefined, value: undefined };
  }

  if (value.indexOf('%') > 0) {
    return { type: SizeType.Percentage, value: parsed };
  }
  return { type: SizeType.Pixel, value: parsed };
};

export const Gap = ({ nodeIds, property, label, isInGroup, cssSelectorId }: Props) => {
  const viewportBreakpoint = useViewportBreakpoint();
  const { responsiveCss, setResponsiveCss } = useResponsiveCss(
    nodeIds,
    CommonNodeProperties.Multiple,
    cssSelectorId,
  );
  const parsedSize = parseValue(responsiveCss[property]);
  const [value, setValue] = useState(parsedSize.value);
  const [type, setType] = useState(parsedSize.type || SizeType.Pixel);

  const onChange = (newValue: any) => {
    setValue(newValue);
    setResponsiveCss({
      [property]: isNumber(newValue)
        ? `${newValue}${type === SizeType.Percentage ? '%' : 'px'}`
        : undefined,
    });
  };

  const onChangeType = (newValue: SizeType) => {
    setType(newValue);
    setResponsiveCss({
      [property]: isNumber(value)
        ? `${value}${newValue === SizeType.Percentage ? '%' : 'px'}`
        : undefined,
    });
  };
  useEffect(() => {
    const newVieportValue = parseValue(responsiveCss[property]);
    setValue(newVieportValue.value);
    setType(newVieportValue.type || SizeType.Pixel);
  }, [viewportBreakpoint]);

  useEffect(() => {
    if (parsedSize.value === value && parsedSize.type === type) {
      return;
    }
    if (parsedSize.value !== value) {
      setValue(parsedSize.value);
    }
    if (parsedSize.type !== type) {
      setType(parsedSize.type || SizeType.Pixel);
    }
  }, [parsedSize]);

  return (
    <SizeStepper
      inGroupProps={isInGroup}
      label={label}
      onChangeType={onChangeType}
      name={label}
      type={type}
      value={value}
      onChange={onChange}
      property="gap"
    />
  );
};
